import { ReactNode } from 'react';
import { alpha, CardContent, Chip, Typography, Box, useTheme, Tooltip, Avatar, Divider } from '@mui/material';
import {
  PhotoLibrary as ImageIcon,
  VideoLibrary as VideoIcon,
  LibraryMusic as AudioIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { LLMModelSpecification } from 'api/model';
import LLMIcon from 'components/LLMIcon/LLMIcon';
import { SelectableCard } from 'views/flow/form/SelectableCard';

type Props = {
  model: LLMModelSpecification;
  selected?: boolean;
  isOnServer?: boolean;
  height?: number;
  action?: ReactNode;
};

const formatTokens = (tokens?: number | null) => {
  if (!tokens || tokens <= 0) return null;
  return tokens >= 1000 ? `${(tokens / 1000).toFixed(0)}K` : tokens.toString();
};

const formatCost = (cost?: number | null) => {
  if (!cost || cost <= 0) return null;
  return `$${(cost * 1000000).toFixed(2)}/1M`;
};

const ModalityIcon = ({
  IconComponent,
  supported,
  label,
}: {
  IconComponent: React.ElementType;
  supported: boolean;
  label: string;
}) => {
  const theme = useTheme();
  return (
    <Tooltip title={`${label} input ${supported ? 'supported' : 'not supported'}`} arrow>
      <Avatar
        sx={{
          width: 26,
          height: 26,
          backgroundColor: supported ? alpha(theme.palette.primary.main, 0.18) : 'transparent',
          border: supported
            ? `1px solid ${alpha(theme.palette.primary.main, 0.35)}`
            : `1px dashed ${alpha(theme.palette.info.main, 0.28)}`,
        }}
      >
        <IconComponent sx={{ fontSize: 16, color: alpha(theme.palette.info.main, supported ? 1 : 0.32) }} />
      </Avatar>
    </Tooltip>
  );
};

const MetricColumn = ({ label, tokens, cost }: { label: string; tokens?: number | null; cost?: number | null }) => {
  const tokenText = formatTokens(tokens);
  const costText = formatCost(cost);
  return (
    <Box className="flex min-w-0 flex-col">
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: '0.08em', lineHeight: 1.6, fontSize: '0.62rem', fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
        {tokenText ?? '—'}
        {tokenText && (
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
            {' '}
            tok
          </Box>
        )}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
        {costText ?? '—'}
      </Typography>
    </Box>
  );
};

const ModelSpecificationCard = ({ model, selected = false, isOnServer = false, height = 212, action }: Props) => {
  const theme = useTheme();

  return (
    <SelectableCard variant="outlined" selected={selected} width="100%" height={height}>
      {action}
      <CardContent className="flex h-full flex-col gap-2" sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        {isOnServer && (
          <Tooltip title="Available on the Ollama server you synced with" arrow placement="top">
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label="On server"
              size="small"
              color="success"
              variant="outlined"
              sx={{ position: 'absolute', top: 8, right: 8, height: 22, fontSize: 11, fontWeight: 600 }}
            />
          </Tooltip>
        )}

        <Box className="flex items-center gap-2 pr-7">
          <LLMIcon provider={model.provider} model={model.name} size={24} />
          <Typography
            variant="subtitle1"
            className="line-clamp-2 leading-tight"
            sx={{ fontWeight: 700, color: theme.palette.text.primary }}
          >
            {model.name}
          </Typography>
        </Box>

        <Typography
          variant="caption"
          className="line-clamp-2 min-h-8"
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            opacity: model.description ? 1 : 0.6,
          }}
        >
          {model.description || 'No description provided.'}
        </Typography>

        <Box className="mt-auto flex flex-col gap-2">
          <Box className="flex items-center gap-1.5">
            <ModalityIcon IconComponent={ImageIcon} supported={!!model.image_input} label="Image" />
            <ModalityIcon IconComponent={VideoIcon} supported={!!model.video_input} label="Video" />
            <ModalityIcon IconComponent={AudioIcon} supported={!!model.audio_input} label="Audio" />
            <ModalityIcon IconComponent={PdfIcon} supported={!!model.pdf_input} label="PDF" />
          </Box>
          <Divider sx={{ borderColor: 'divider' }} />
          <Box className="grid grid-cols-2 gap-3">
            <MetricColumn label="Input" tokens={model.max_input_tokens} cost={model.input_cost_per_token} />
            <MetricColumn label="Output" tokens={model.max_output_tokens} cost={model.output_cost_per_token} />
          </Box>
        </Box>
      </CardContent>
    </SelectableCard>
  );
};

export default ModelSpecificationCard;
